(() => {
  const track = [0, 1, 2, 3, 4, 5, 6];
  const positionClasses = track.map((position) => `is-position-${position}`);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const LOSE_STEPS = [1, 2, 4];
  const PRIZE_STEPS = {
    317: 6, // 1st
    318: 3, // 2nd
    319: 5, // 3rd
    320: null, // no prize → random from LOSE_STEPS
  };

  const DICE_ANIMATION_MS = 2000;
  const ACT_ID = "2026_UTMB_UK";
  const MSG_ENTER_EMAIL = "Please enter your email first.";
  const MSG_EMAIL_LIMIT = "Email exceeded the limit for participation.";
  const MSG_IP_LIMIT = "The number of requests from the same IP address exceeded the limit.";
  const MSG_SERVER_ERROR = "Something went wrong. Please try again.";
  const SUBSCRIBE_MODULE = "26utmb_subscription";

  const instances = new Set();

  const getKlaviyoFormRoot = (root) =>
    root?.querySelector?.('[class^="klaviyo-form-"]') || null;

  const getKlaviyoFormId = (lottery, form) => {
    const fromData = lottery?.getAttribute?.("data-klaviyo-form-id");
    if (fromData) return fromData;

    const className = [...(form?.classList || [])].find((name) =>
      name.startsWith("klaviyo-form-")
    );
    return className ? className.slice("klaviyo-form-".length) : "";
  };

  const pushSubscribeGa4 = (status, email) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: "ga4Event",
      event_name: "subscribe",
      event_parameters: {
        module: SUBSCRIBE_MODULE,
        status,
        email: email || "",
        content_name: "",
      },
    });
  };

  const wait = (duration) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });

  const pickLoseSteps = () => {
    if (!window.crypto?.getRandomValues) {
      return LOSE_STEPS[Math.floor(Math.random() * LOSE_STEPS.length)];
    }

    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return LOSE_STEPS[values[0] % LOSE_STEPS.length];
  };

  const getStepsForPrizeId = (actPriceId) => {
    const id = Number(actPriceId);
    if (id === 320) return pickLoseSteps();
    return PRIZE_STEPS[id] || null;
  };

  const getErrorMessage = (actStatus) => {
    if (actStatus === "no_finish") return MSG_EMAIL_LIMIT;
    if (actStatus === "no_finish_ip") return MSG_IP_LIMIT;
    return MSG_SERVER_ERROR;
  };

  const ajaxLottery = (email) => {
    const formData = new FormData();
    formData.append("mb_id", email);
    formData.append("act_id", ACT_ID);

    return fetch("https://global.shokz.com/lottery/openapi_prize.php", {
      method: "POST",
      body: formData,
    }).then((res) => {
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      return res.json();
    });
  };

  const formatStatus = (template, steps) =>
    String(template || "").replaceAll("{steps}", String(steps));

  const getDiceTemplate = (lottery, steps) =>
    lottery.querySelector(`template[data-utmb26-lottery-dice="${steps}"]`);

  const showDiceForSteps = (lottery, slot, steps) => {
    if (!slot) return;

    const template = getDiceTemplate(lottery, steps);
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    slot.replaceChildren(fragment);
  };

  const parseModalConfig = (lottery) => {
    const node = lottery.querySelector("[data-utmb26-lottery-modal-config]");
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const setupModal = (lottery) => {
    const modal = lottery.querySelector("[data-utmb26-lottery-modal]");
    const dialog = lottery.querySelector("[data-utmb26-lottery-modal-dialog]");
    const closeButtons = lottery.querySelectorAll("[data-utmb26-lottery-modal-close]");
    const titleEl = lottery.querySelector("[data-utmb26-lottery-modal-title]");
    const subtitleEl = lottery.querySelector("[data-utmb26-lottery-modal-subtitle]");
    const heartEl = lottery.querySelector("[data-utmb26-lottery-modal-heart]");
    const imageEl = lottery.querySelector("[data-utmb26-lottery-modal-image]");
    const config = parseModalConfig(lottery);

    if (!modal || !dialog || !titleEl || !subtitleEl || !config) {
      return { open: () => {} };
    }

    let lastFocus = null;

    const close = () => {
      modal.classList.remove("is-open");
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("utmb26-lottery-modal-open");
      if (lastFocus && typeof lastFocus.focus === "function") {
        lastFocus.focus();
      }
    };

    const open = (position) => {
      const entry = config.positions?.[String(position)];
      if (!entry) return;

      const isWin = entry.type === "win";
      modal.dataset.variant = isWin ? "win" : "lose";

      if (isWin) {
        const prize = entry.prize || "Prize";
        const line2 = String(config.winTitleLine2 || "The {prize} is yours.").replaceAll(
          "{prize}",
          prize
        );
        titleEl.innerHTML = `${config.winTitleLine1 || "You're in."}<br>${line2}`;
        subtitleEl.textContent = config.winSubtitle || "";
        if (heartEl) heartEl.hidden = true;
        if (imageEl) {
          imageEl.src = entry.image || imageEl.src;
          imageEl.alt = prize;
          imageEl.hidden = false;
          imageEl.style.display = "block";
        }
      } else {
        titleEl.textContent = config.loseTitle || "You were so close to winning!";
        subtitleEl.innerHTML = config.loseSubtitle || "";
        if (imageEl) {
          imageEl.hidden = true;
          imageEl.removeAttribute("alt");
          imageEl.style.display = "none";
        }
        if (heartEl) heartEl.hidden = false;
      }

      lastFocus = document.activeElement;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("utmb26-lottery-modal-open");
      requestAnimationFrame(() => {
        modal.classList.add("is-open");
      });

      const closeBtn = modal.querySelector("[data-utmb26-lottery-modal-close]");
      closeBtn?.focus();
    };

    closeButtons.forEach((button) => {
      button.addEventListener("click", close);
    });

    modal.addEventListener("click", (event) => {
      if (event.target === modal) close();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) {
        close();
      }
    });

    return { open, close };
  };

  const readEmailFromForm = (form) => {
    const input = form?.querySelector(
      'input[type="email"], input:not([type="checkbox"]):not([type="hidden"])'
    );
    return (input?.value || "").trim();
  };

  const setup = (lottery) => {
    if (lottery._utmb26Abort) {
      lottery._utmb26Abort.abort();
      instances.delete(lottery._utmb26Api);
    }

    const abort = new AbortController();
    const { signal } = abort;
    lottery._utmb26Abort = abort;

    const piece = lottery.querySelector("[data-utmb26-lottery-piece]");
    const button = lottery.querySelector("[data-utmb26-lottery-roll]");
    const status = lottery.querySelector("[data-utmb26-lottery-status]");
    const form = getKlaviyoFormRoot(lottery);
    const klaviyoFormId = getKlaviyoFormId(lottery, form);
    const diceSlot =
      lottery.querySelector("[data-utmb26-lottery-dice-slot]") ||
      button?.querySelector(".utmb26-lottery__dice") ||
      button;

    if (!piece || !button || !status) return;

    const modalApi = setupModal(lottery);

    let currentIndex = 0;
    let isMoving = false;
    let pendingSteps = null;
    let hasRolled = false;
    let cachedEmail = "";
    let isFetchingPrize = false;
    let hasHandledKlaviyoSuccess = false;
    let pendingKlaviyoMessage = null;

    const setStatus = (text) => {
      status.textContent = text;
      status.classList.toggle("is-visible", Boolean(text));
    };

    const applyKlaviyoMessage = () => {
      if (!form || !pendingKlaviyoMessage) return false;
      const title = form.querySelector("h1 span") || form.querySelector("h1");
      if (!title) return false;
      title.textContent = pendingKlaviyoMessage;
      form.classList.add("is-success", "is-error");
      return true;
    };

    const setKlaviyoMessage = (text) => {
      pendingKlaviyoMessage = text;
      applyKlaviyoMessage();
    };

    const cacheEmail = (email) => {
      const next = (email || readEmailFromForm(form) || "").trim();
      if (next) cachedEmail = next;
      return cachedEmail;
    };

    const requestPrizeForEmail = async (emailFromEvent) => {
      if (isFetchingPrize || pendingSteps || hasRolled) return;

      const email = cacheEmail(emailFromEvent);
      if (!email) {
        setStatus(MSG_ENTER_EMAIL);
        return;
      }

      isFetchingPrize = true;
      let shouldAutoRoll = false;

      try {
        const data = await ajaxLottery(email);

        if (data?.status === "ok") {
          const steps = getStepsForPrizeId(data.data?.act_price_id);
          if (!steps) {
            setKlaviyoMessage(MSG_SERVER_ERROR);
            setStatus(MSG_SERVER_ERROR);
          } else {
            pendingKlaviyoMessage = null;
            pendingSteps = steps;
            setStatus(lottery.dataset.idleText || "Ready to roll");
            shouldAutoRoll = true;
          }
        } else {
          const errorMessage = getErrorMessage(data?.data?.act_status);
          setKlaviyoMessage(errorMessage);
          setStatus(errorMessage);
        }
      } catch (error) {
        console.error("UTMB26 lottery error:", error);
        setKlaviyoMessage(MSG_SERVER_ERROR);
        setStatus(MSG_SERVER_ERROR);
      } finally {
        isFetchingPrize = false;
      }

      if (shouldAutoRoll) {
        await movePiece();
      }
    };

    const markKlaviyoSuccessUi = () => {
      if (!form || hasHandledKlaviyoSuccess) {
        applyKlaviyoMessage();
        return true;
      }

      const thanks = form.querySelector("h1 span, h1");
      if (!thanks) return false;

      const text = thanks.textContent?.trim() || "";
      if (!/thanks for your registration/i.test(text) && !pendingKlaviyoMessage) {
        return false;
      }

      hasHandledKlaviyoSuccess = true;
      form.classList.add("is-success");
      applyKlaviyoMessage();
      return true;
    };

    if (form) {
      form.addEventListener("input", () => cacheEmail(), { signal, capture: true });
      form.addEventListener("change", () => cacheEmail(), { signal, capture: true });
      form.addEventListener(
        "pointerdown",
        (event) => {
          if (event.target.closest("button")) cacheEmail();
        },
        { signal, capture: true }
      );

      if (!markKlaviyoSuccessUi()) {
        const observer = new MutationObserver(() => {
          if (markKlaviyoSuccessUi()) observer.disconnect();
        });
        observer.observe(form, { childList: true, subtree: true, characterData: true });
        signal.addEventListener("abort", () => observer.disconnect());
      }
    }

    const heading = lottery.querySelector(".utmb26-lottery__heading");

    const shakeHeading = () => {
      if (!heading || reduceMotion.matches) return;
      heading.classList.remove("is-shake");
      void heading.offsetWidth;
      heading.classList.add("is-shake");
    };

    if (heading) {
      heading.addEventListener(
        "animationend",
        () => {
          heading.classList.remove("is-shake");
        },
        { signal }
      );
    }

    const moveOneSpace = async () => {
      currentIndex = (currentIndex + 1) % track.length;
      const nextPosition = track[currentIndex];

      piece.classList.remove(...positionClasses, "is-stepping");
      lottery.classList.remove(...positionClasses, "is-stepping");
      piece.classList.add(`is-position-${nextPosition}`);
      lottery.classList.add(`is-position-${nextPosition}`);

      if (!reduceMotion.matches) {
        void piece.offsetWidth;
        piece.classList.add("is-stepping");
        await wait(430);
      }
    };

    const movePiece = async () => {
      if (isMoving || hasRolled) return;

      if (!pendingSteps) {
        setStatus(MSG_ENTER_EMAIL);
        shakeHeading();
        return;
      }

      const steps = pendingSteps;
      pendingSteps = null;
      isMoving = true;
      hasRolled = true;
      button.disabled = true;
      lottery.setAttribute("aria-busy", "true");

      showDiceForSteps(lottery, diceSlot, steps);
      setStatus(formatStatus(lottery.dataset.movingText, steps));

      if (!reduceMotion.matches) {
        await wait(DICE_ANIMATION_MS);
      }

      for (let step = 0; step < steps; step += 1) {
        await moveOneSpace();
      }

      piece.classList.remove("is-stepping");
      setStatus(formatStatus(lottery.dataset.resultText, steps));
      lottery.removeAttribute("aria-busy");
      isMoving = false;

      const finalPosition = track[currentIndex];
      if (finalPosition > 0) {
        modalApi.open(finalPosition);
      }
    };

    const api = {
      lottery,
      formId: klaviyoFormId,
      handleEmailSubmit: (email) => {
        markKlaviyoSuccessUi();
        return requestPrizeForEmail(email);
      },
    };

    lottery._utmb26Api = api;
    instances.add(api);

    button.addEventListener("click", movePiece, { signal });
    lottery.dataset.utmb26LotteryInitialized = "true";

    if ("IntersectionObserver" in window && lottery.dataset.utmb26LotteryImpressed !== "true") {
      const impressionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event_parameters: null });
          window.dataLayer.push({
            event: "ga4Event",
            event_name: "module_impression",
            event_parameters: {
              module: "26utmb_subscribe",
            },
          });

          lottery.dataset.utmb26LotteryImpressed = "true";
          impressionObserver.unobserve(entry.target);
        });
      });

      impressionObserver.observe(lottery);
    }
  };

  const init = (root = document) => {
    if (root.matches?.("[data-utmb26-lottery]")) {
      setup(root);
    }

    root.querySelectorAll?.("[data-utmb26-lottery]").forEach(setup);
  };

  if (!window.__utmb26KlaviyoFormsBound) {
    window.__utmb26KlaviyoFormsBound = true;
    window.addEventListener("klaviyoForms", (event) => {
      const detail = event.detail || {};
      const email = detail.metaData?.$email || "";

      const matchedApis = [];
      instances.forEach((api) => {
        if (!api?.lottery || !document.contains(api.lottery)) {
          instances.delete(api);
          return;
        }
        if (detail.formId && api.formId && detail.formId !== api.formId) return;
        matchedApis.push(api);
      });

      if (!matchedApis.length) return;

      if (detail.type === "submit") {
        pushSubscribeGa4("success", email);
        matchedApis.forEach((api) => api.handleEmailSubmit(email));
        return;
      }

      // Klaviyo does not always emit a dedicated fail event; treat known failures if present.
      if (detail.type === "failedSubmit" || detail.type === "error") {
        pushSubscribeGa4("fail", email);
      }
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  window.utmb26Lottery = { init };
  init();
})();
